import { Component, EventEmitter, inject, Output } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BehaviorSubject, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { CalendarService } from 'src/app/_services/calendar.service';
import { ModalService } from 'src/app/_services/modal.service';
import { ProfileService } from 'src/app/_services/profile.service';
import { SectionService } from 'src/app/_services/section.service';
import { RequestCalendar } from 'src/app/models/request-calendar.interface';
import { BaseComponent } from '../components/base/base.component';
import { SecondStepSicknessComponent } from '../components/calendar/request-modal/second-step/second-step-sickness/second-step-sickness.component';
import { SicknessRequestComponent } from '../components/calendar/request-modal/SicknessRequest/SicknessRequest.component';
import { SectionModalComponent } from './section-modal/section-modal.component';

@Component({
  selector: 'app-new-sidebar',
  templateUrl: './new-sidebar.component.html',
  styleUrls: ['./new-sidebar.component.css']
})
export class NewSidebarComponent extends BaseComponent {
  private readonly profileService: ProfileService = inject(ProfileService);
  private readonly sectionsService: SectionService = inject(SectionService);
  private readonly nzModalService: NzModalService = inject(NzModalService);
  private readonly modalService: ModalService = inject(ModalService);
  private readonly calendarService: CalendarService = inject(CalendarService);

  @Output() sidebarHovered = new EventEmitter<boolean>();

  menus: any[] = [
    {
      title: 'Collaborazione',
      icon: 'partnership',
      subMenus: [
        {
          title: 'Progetti',
          icon: 'team',
          pathLink: 'progetti',
          section: 'i miei team'
        },
        {
          title: 'Timesheet',
          icon: 'timesheet',
          pathLink: 'timesheet',
          section: 'time sheet'
        },
        {
          title: 'Calendario',
          icon: 'calendar',
          pathLink: 'calendar',
          section: 'calendario'
        }
      ]
    },
    {
      title: 'Feedback',
      icon: 'feedback',
      subMenus: [
        {
          title: 'SBI',
          icon: 'sbi',
          pathLink: 'sbi',
          section: 'sbi'
        },
        {
          title: 'Sondaggi',
          icon: 'survey',
          pathLink: 'sondaggi',
          section: 'sondaggi'
        }
      ]
    },
    {
      title: 'Statistiche',
      canShow: false,
      icon: 'chart-up',
      subMenus: [
        {
          title: 'Monitoraggio',
          icon: 'bar-chart',
          pathLink: 'monitoraggio',
          section: 'monitoraggio'
        },
        {
          title: 'Fatturazione',
          icon: 'euro',
          pathLink: 'fatturazione',
          section: 'fatturazione'
        }
      ]
    }
  ];
  openMenu: { [name: string]: boolean } = {};
  private sectionsStatus: { [key: string]: boolean } = {};
  isAdmin: boolean;
  pathURL: BehaviorSubject<string> = new BehaviorSubject('');
  havePendingIllnsessRequest: boolean = false;
  pendingIllness: RequestCalendar;
  private readonly destroy$: Subject<void> = new Subject();

  constructor() {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.isAdmin = await this.profileService.isAdmin();
    this.menus.forEach((_, index) => {
      this.openMenu[index] = false;
    });

    this.getSections();
    this.getPendingIllnessForLoggedUser();
  }

  /**
   * Funzione per recuperare tutte le sezioni
   */
  getSections() {
    this.sectionsService
      .getSectionsStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.sectionsStatus = value;
      });
  }

  openHandler(index: number): void {
    for (const key in this.openMenu) {
      this.openMenu[key] = +key === index;
    }
  }

  /**
   * funzione che serve per capire se l'utente loggato può accedere sempre alla sezione
   * @param section nome della sezione
   * @returns boolean
   */
  canAccessSection(section: string): boolean {
    if (this.isAdmin) {
      return true;
    }
    return this.sectionsService.isSectionEnabled(section);
  }

  /**
   * funzione che serve per capire se una sezione è attiva oppure no
   * @param section nome della sezione
   * @returns boolean
   */
  isSectionActive(section: string): boolean {
    return this.sectionsStatus[section];
  }

  onMouseEnter() {
    this.sidebarHovered.emit(true);
  }

  onMouseLeave() {
    this.sidebarHovered.emit(false);
  }

  openModalSections() {
    const modalRef = this.nzModalService.create({
      nzContent: SectionModalComponent,
      nzFooter: null,
      nzClosable: false,
      nzWidth: '350px',
      nzClassName: 'custom-modal-sections',
      nzComponentParams: {
        menus: this.menus,
        sectionsStatus: this.sectionsStatus
      }
    });

    const instance = modalRef.getContentComponent();

    instance.updateSections.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getIndexSection();
    });
  }

  /**
   * funzione per richiamare API per le sezioni
   */
  async getIndexSection() {
    const res: any = await firstValueFrom(this.sectionsService.loadSections());
    this.sectionsStatus = res.Sections;
  }

  allSubmenuDisabled(menu: any): boolean {
    return menu.subMenus.every(
      (subMenu: any) => !this.isSectionActive(subMenu.section)
    );
  }

  shouldShowMenu(menu: any): boolean {
    if (this.isAdmin) return true;

    // Se il menu è "Statistiche", nascondilo per i non-admin
    if (menu.title === 'Statistiche') return false;

    // Altrimenti, nascondi se tutte le sottosezioni sono disattivate
    return !this.allSubmenuDisabled(menu);
  }

  getPendingIllnessForLoggedUser() {
    this.calendarService
      .getPendingIllnessForLoggedUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res.result) {
          this.havePendingIllnsessRequest = true;
          this.pendingIllness = res.result;
        }
      });
  }

  /**
   * funzione che si occupa di aprire la modale per la richiesta di malattia rapida,
   * se l'utente ha già una richiesta di malattia in corso,
   * apre direttamente la seconda modale con i dettagli della richiesta e la possibilità
   * di caricare il certificato medico,
   * altirmenti apre la prima modale dove notifichi l'assenza
   */
  openSicknessModal() {
    const isEditingExisting = this.havePendingIllnsessRequest;

    const component = isEditingExisting
      ? SecondStepSicknessComponent
      : SicknessRequestComponent;

    const modalParams = isEditingExisting
      ? {
          request: this.pendingIllness,
          user: null,
          date: this.pendingIllness.start_date,
          edit: false
        }
      : {
          date: new Date()
        };

    const modalRef = this.modalService.openModal(component, 640, modalParams);
    this.subscribeToModalClose(modalRef);
  }

  private subscribeToModalClose(modalRef: any): void {
    modalRef.afterClose.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getPendingIllnessForLoggedUser();
    });
  }
}
